import { useRolodexEntries } from 'features/rolodex/hooks/useRolodexEntries'
import { useMemo } from 'react'
import { RolodexEntryType } from 'types'

/** An entry the user can @-mention in the chat input. */
export interface MentionableEntry {
  id: string
  name: string
  type: RolodexEntryType
}

export interface UseMentionableEntriesResult {
  entries: MentionableEntry[]
  isLoading: boolean
}

const DEFAULT_ENTRY_TYPES: ReadonlyArray<RolodexEntryType> = [
  RolodexEntryType.UNIT,
]

/**
 * Fetch the entries the current user may @-mention, scoped to the active site
 * (falling back to org), for the given `entryTypes`. To make a new kind of
 * entry mentionable (e.g. components or inspections), just add its
 * `RolodexEntryType` to `entryTypes` -- nothing else in this hook needs to
 * change.
 *
 * RBAC is enforced server-side by Rolodex via the authorized axios in
 * `useRolodexEntries`, so this only returns entries the user is permitted to
 * see.
 */
export const useMentionableEntries = (
  orgId: string | null,
  siteId: string | null,
  entryTypes: ReadonlyArray<RolodexEntryType> = DEFAULT_ENTRY_TYPES
): UseMentionableEntriesResult => {
  // Stable primitive key so callers don't have to memoize the `entryTypes`
  // array to keep the query (and its SWR cache entry) stable.
  const typesKey = entryTypes.join(',')

  const query = useMemo(() => {
    const type = typesKey ? (typesKey.split(',') as RolodexEntryType[]) : []
    if (!type.length) return undefined
    // Prefer scoping by site (the tighter, more relevant set); fall back to org.
    if (siteId) {
      return { type, tags: { site: siteId } }
    }
    if (orgId) {
      return { type, tags: { organization: orgId } }
    }
    return undefined
  }, [orgId, siteId, typesKey])

  const { entries, isLoading } = useRolodexEntries(query)

  const mentionable = useMemo<MentionableEntry[]>(
    () =>
      entries
        .map((entry) => {
          const rawName = entry.data?.name
          return {
            id: entry.id,
            name: typeof rawName === 'string' && rawName ? rawName : entry.id,
            type: entry.type,
          }
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    [entries]
  )

  return { entries: mentionable, isLoading }
}
